import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  formatDateToDDMMYYYY,
  getCurrentPinType,
  getQuoteInvoiceListScope,
  resolveQuoteListApiType,
  QUOTE_INVOICE_LIST_SCOPE_EVENT,
} from "./const";
import { useNavigate } from "react-router-dom";
import AssignKarigarModal from "../components/AssignKarigarModal";
import ReceiveUpdateModal from "../components/ReceiveUpdateModal";
import CancelOrderModal from "../components/CancelOrderModal";
import CustomOrderPdfModal from "../components/CustomOrderPdfModal";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import {
  listBankAccounts,
  listCustomOrders,
  showCustomOrder,
  isErpApiSuccess,
  settlementCustomOrder,
} from "../erpClient";
import { parseApiPagination } from "../utils/parseApiPagination";

import { createErpSocket } from "../utils/socketIo";
import Select from "react-select";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  resolveCustomOrderShowPayload,
  extractCustomOrderListRows,
  createCustomOrderIdempotencyKey,
  isCustomOrdersApiSuccess,
} from "../utils/customOrdersErpAdapter";

const SETTLEMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "old_gold", label: "Old Gold" },
  { value: "old_silver", label: "Old Silver" },
  { value: "kasar", label: "Kasar" },
  { value: "scheme", label: "Scheme" },
  { value: "order", label: "Order" },
  { value: "os", label: "OS" },
  { value: "wallet", label: "Wallet" },
];

const SETTLEMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

const createDefaultSettlementRows = () => [
  {
    id: 1,
    pay_mode: "",
    type: "",
    remarks: "",
    amount: "",
  },
];

const parseNumericValue = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? 0 : value;
  }
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const numeric = parseFloat(cleaned);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const getCustomOrderFinalAmount = (order = {}) => {
  const entries = order.custom_order_entry;
  if (Array.isArray(entries) && entries.length > 0) {
    return entries.reduce(
      (sum, item) =>
        sum + parseNumericValue(item.totalamt ?? item.total_amount ?? 0),
      0,
    );
  }
  return parseNumericValue(
    order.final_amt ?? order.final_amount ?? order.total ?? 0,
  );
};

/** Prefer API adv_paid; fall back to summing payments when list payload has no adv_paid. */
const getCustomOrderAdvPaid = (order = {}) => {
  const fromApi = order.adv_paid ?? order.advance_paid ?? order.advPaid;
  if (fromApi != null && fromApi !== "") {
    return parseNumericValue(fromApi);
  }
  if (!Array.isArray(order.payments) || order.payments.length === 0) {
    return 0;
  }
  return order.payments.reduce(
    (sum, payment) =>
      sum +
      parseNumericValue(
        payment.aavak_amt ??
        payment.total_amount ??
        payment.amt ??
        payment.amount ??
        0,
      ),
    0,
  );
};

/** Prefer API grand_total (same as Invoice / Sales list); recompute only when missing. */
const getCustomOrderGrandTotal = (order = {}) => {
  const fromApi = parseNumericValue(order.grand_total ?? order.grandTotal);
  if (
    order.grand_total != null &&
    order.grand_total !== "" &&
    !Number.isNaN(fromApi)
  ) {
    return fromApi;
  }
  if (
    order.grandTotal != null &&
    order.grandTotal !== "" &&
    !Number.isNaN(parseNumericValue(order.grandTotal))
  ) {
    return parseNumericValue(order.grandTotal);
  }

  const finalAmt = getCustomOrderFinalAmount(order);
  const roundOff = parseNumericValue(order.round_off);

  let gstAmt = parseNumericValue(
    order.gst_amount ?? order.gst_amt ?? order.tax_amount,
  );

  if (!gstAmt && finalAmt > 0) {
    const gstPercent = parseNumericValue(order.gst_percent ?? 3);
    gstAmt = (finalAmt * gstPercent) / 100;
  }

  return Math.max(finalAmt + gstAmt - roundOff, 0);
};

const normalizeCustomOrderStatus = (row) => {
  const explicit = row?.status_code ?? row?.legacy_status;
  if (explicit !== undefined && explicit !== null && explicit !== "") {
    return String(explicit);
  }
  const s = String(row?.status ?? "")
    .trim()
    .toUpperCase();
  if (s === "OPEN" || s === "ACTIVE" || s === "1") return "1";
  if (s === "INACTIVE" || s === "0") return "0";
  if (s === "CANCELLED" || s === "3") return "3";
  return "";
};

const normalizeCustomOrderRow = (row = {}) => ({
  ...row,
  co_number:
    row.co_number ?? row.order_number ?? row.doc_number ?? row.tag_no ?? "",
  order_number: row.order_number ?? row.doc_number ?? row.co_number ?? "",
  customer_name:
    row.customer_name ?? row.user_name ?? row.name ?? row.customer ?? "",
  customer_mobile: row.customer_mobile ?? row.user_mobile ?? row.mobile ?? "",
  employee_name:
    row.by_user_name ??
    row.user_name ??
    row.created_by_name ??
    row.employee ??
    "N/A",
  final_amt:
    row.final_amt ?? row.final_amount ?? row.grand_total ?? row.total ?? 0,
  total: row.total ?? row.final_amount ?? row.final_amt ?? row.grand_total ?? 0,
  adv_paid: row.adv_paid ?? row.advance_paid ?? row.advPaid ?? 0,
  custom_order_entry: (
    row.custom_order_entry ??
    row.custom_order ??
    row.entries ??
    row.items ??
    []
  ).map((item) => ({
    ...item,
    totalamt: item.totalamt ?? item.total_amount ?? 0,
  })),
  status: normalizeCustomOrderStatus(row),
});

function CustomOrderEntryTable({ masterGoldRate, masterSilverRate }) {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const canCancelOrder = hasPermission(PERMISSIONS.ORDER_CANCEL);
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quoteListScope, setQuoteListScope] = useState(
    getQuoteInvoiceListScope,
  );
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [selectedItems, setSelectedItems] = useState({}); // Track selected items by order id and item id
  const [showAssignKarigarModal, setShowAssignKarigarModal] = useState(false);
  const [assignKarigarOrderData, setAssignKarigarOrderData] = useState(null);
  const [showReceiveUpdateModal, setShowReceiveUpdateModal] = useState(false);
  const [receiveUpdateOrderData, setReceiveUpdateOrderData] = useState(null);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null);
  const [cancelSelectedItems, setCancelSelectedItems] = useState([]);
  const [isWholeOrderCancel, setIsWholeOrderCancel] = useState(false);
  const [bankAccountsList, setBankAccountsList] = useState([]);
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [showPaymentSettlementModal, setShowPaymentSettlementModal] =
    useState(false);
  const [selectedOrderForSettlement, setSelectedOrderForSettlement] =
    useState(null);
  const [settlementOrderDetails, setSettlementOrderDetails] = useState(null);
  const [existingSettlementPayments, setExistingSettlementPayments] = useState(
    [],
  );
  const [customSettlementRows, setCustomSettlementRows] = useState(
    createDefaultSettlementRows(),
  );
  const [isSettlementLoading, setIsSettlementLoading] = useState(false);
  const [isSettlementSaving, setIsSettlementSaving] = useState(false);
  const [isPreviousPaymentsExpanded, setIsPreviousPaymentsExpanded] =
    useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedOrderForPdf, setSelectedOrderForPdf] = useState(null);
  const hasFetchedBanks = React.useRef(false);
  /** Reused on settlement retries so duplicate ledger posts are avoided */
  const settlementIdempotencyKeyRef = useRef(null);

  const fetchOrders = useCallback(
    async (page = 1, limit = 10, search = "") => {
      setIsLoading(true);
      try {
        const response = await listCustomOrders({
          search: search.trim(),
          page,
          limit,
          type: resolveQuoteListApiType("list"),
        });
        const rows = extractCustomOrderListRows(response);

        if (isErpApiSuccess(response) || response?.message === "radha") {
          const mapped = rows.map(normalizeCustomOrderRow);
          mapped.sort((a, b) => Number(b.id) - Number(a.id));
          setOrders(mapped);
          setTotalCount(parseApiPagination(response, mapped.length).total);
        } else {
          setOrders([]);
          setTotalCount(0);
          if (response?.message) toast.error(response.message);
        }
      } catch (error) {
        setOrders([]);
        setTotalCount(0);
        toast.error(
          error.response?.data?.message || "Failed to fetch custom orders",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [quoteListScope],
  );

  const fetchBankAccounts = async () => {
    try {
      const response = await listBankAccounts();

      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const fetchedBankAccounts = response.data.bank_accounts
          .map((bank) => {
            const chartAccountId =
              bank.account_id ??
              bank.accountId ??
              bank.linked_account_id ??
              bank.accounts_id ??
              bank.id;
            return {
              id: chartAccountId,
              bank_name: bank.bank_name || "",
              account_number: bank.account_number || "",
              account_type: bank.account_type || "",
              status: bank.status,
            };
          })
          .filter((bank) => bank.status === 1);

        fetchedBankAccounts.sort((a, b) => Number(b.id) - Number(a.id));
        setBankAccountsList(fetchedBankAccounts);
      } else {
        setBankAccountsList([]);
      }
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setBankAccountsList([]);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchOrders(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchOrders]);

  // Navbar Quotation | All → list vs mixed
  useEffect(() => {
    const handleScopeChange = (event) => {
      const next =
        event?.detail?.scope === "all" ? "all" : getQuoteInvoiceListScope();
      setQuoteListScope(next);
      setCurrentPage(1);
    };
    window.addEventListener(QUOTE_INVOICE_LIST_SCOPE_EVENT, handleScopeChange);
    return () => {
      window.removeEventListener(
        QUOTE_INVOICE_LIST_SCOPE_EVENT,
        handleScopeChange,
      );
    };
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in CustomOrderEntryTable, refetching orders...",
        event.detail,
      );
      hasFetchedBanks.current = false;
      setCurrentPage(1);
      fetchOrders(1, itemsPerPage, searchQuery);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchOrders]);

  const ensureBankAccountsFetched = async () => {
    if (hasFetchedBanks.current) return;
    await fetchBankAccounts();
    hasFetchedBanks.current = true;
  };

  // Enrich orders with customer names using show_custom_order by id (uses same API)
  // Removed per-order enrichment to avoid multiple show_custom_order calls

  useEffect(() => {
    if (showPaymentSettlementModal) {
      ensureBankAccountsFetched();
    }
  }, [showPaymentSettlementModal]);

  useEffect(() => {
    if (showCancelOrderModal) {
      ensureBankAccountsFetched();
    }
  }, [showCancelOrderModal]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => { });

    newSocket.on("disconnect", (reason) => {
      // console.log(
      //   "CustomOrderEntryTable: Disconnected from socket server:",
      //   reason,
      // );
    });

    newSocket.on("connect_error", (error) => {
      console.error("CustomOrderEntryTable: Socket connection error:", error);
    });

    // Listen for the created event (emitCreated likely appends "_created")
    newSocket.on("custom_order_created", (data) => {
      // console.log("Socket received custom_order_created event:", data);

      // Check for the actual data structure from your API
      if (!data?.custom_order_details) return;

      const orderData = data.custom_order_details;

      const newOrder = normalizeCustomOrderRow({
        id: orderData.id,
        customer_name:
          orderData.customer_name ||
          orderData.name ||
          orderData.user_name ||
          "",
        co_number: orderData.co_number || "",
        tag_no: orderData.tag_no || "",
        status:
          orderData.status !== null && orderData.status !== undefined
            ? String(orderData.status)
            : "1",
        total: orderData.total || 0,
        final_amt: orderData.final_amt || 0,
        custom_order_entry: orderData.custom_order_entry || [],
        created_at: orderData.created_at || new Date().toISOString(),
        exp_delivery: orderData.exp_delivery || "",
        remaining: orderData.remaining || 0,
        round_off: orderData.round_off || 0,
        note: orderData.note || "",
        payments: orderData.payments || [],
        user_id: orderData.user_id || "",
        by_user: orderData.by_user || "",
        ...orderData,
      });

      setOrders((prev) => {
        const exists = prev.some(
          (order) => String(order.id) === String(newOrder.id),
        );
        if (exists) {
          return prev.map((order) =>
            String(order.id) === String(newOrder.id) ? newOrder : order,
          );
        }
        return [newOrder, ...prev];
      });
    });

    // Listen for update events if they exist
    newSocket.on("custom_order_updated", (data) => {
      // console.log("Socket received custom_order_updated event:", data);

      if (!data?.custom_order_details) return;

      const orderData = data.custom_order_details;

      setOrders((prev) =>
        prev.map((order) =>
          String(order.id) === String(orderData.id)
            ? normalizeCustomOrderRow({
              ...order,
              ...orderData,
              customer_name:
                orderData.customer_name ||
                orderData.name ||
                orderData.user_name ||
                order.customer_name,
              co_number: orderData.co_number || order.co_number,
              tag_no: orderData.tag_no || order.tag_no,
              status: orderData.status ?? order.status,
              total: orderData.total ?? order.total,
              final_amt: orderData.final_amt ?? order.final_amt,
              custom_order_entry:
                orderData.custom_order_entry || order.custom_order_entry,
              payments: orderData.payments || order.payments,
              exp_delivery: orderData.exp_delivery ?? order.exp_delivery,
              remaining: orderData.remaining ?? order.remaining,
              round_off: orderData.round_off ?? order.round_off,
              note: orderData.note ?? order.note,
            })
            : order,
        ),
      );
    });

    // Listen for assign karigar events (from customAssignKarigar API)
    newSocket.on("custom_order_updated", (data) => {
      // console.log(
      //   "Socket received custom_order_updated event (assign karigar/cancel order):",
      //   data
      // );

      if (!data?.custom_order) return;

      const orderData = data.custom_order;

      setOrders((prev) =>
        prev.map((order) =>
          String(order.id) === String(orderData.id)
            ? normalizeCustomOrderRow({
              ...order,
              ...orderData,
              customer_name:
                orderData.customer_name ||
                orderData.name ||
                orderData.user_name ||
                order.customer_name,
              co_number: orderData.co_number || order.co_number,
              tag_no: orderData.tag_no || order.tag_no,
              status: orderData.status ?? order.status,
              total: orderData.total ?? order.total,
              final_amt: orderData.final_amt ?? order.final_amt,
              custom_order_entry:
                orderData.custom_order_entry || order.custom_order_entry,
              payments: orderData.payments || order.payments,
              exp_delivery: orderData.exp_delivery ?? order.exp_delivery,
              remaining: orderData.remaining ?? order.remaining,
              round_off: orderData.round_off ?? order.round_off,
              note: orderData.note ?? order.note,
              user_id: orderData.user_id ?? order.user_id,
              by_user: orderData.by_user ?? order.by_user,
            })
            : order,
        ),
      );
    });

    // Listen for cancel order events
    newSocket.on("cancel_order_created", (data) => {
      // console.log("Socket received cancel_order_created event:", data);

      if (!data?.custom_order) return;

      const orderData = data.custom_order;

      setOrders((prev) =>
        prev.map((order) =>
          String(order.id) === String(orderData.id)
            ? normalizeCustomOrderRow({
              ...order,
              ...orderData,
              customer_name:
                orderData.customer_name ||
                orderData.name ||
                orderData.user_name ||
                order.customer_name,
              co_number: orderData.co_number || order.co_number,
              tag_no: orderData.tag_no || order.tag_no,
              status: orderData.status ?? order.status,
              total: orderData.total ?? order.total,
              final_amt: orderData.final_amt ?? order.final_amt,
              custom_order_entry:
                orderData.custom_order_entry || order.custom_order_entry,
              payments: orderData.payments || order.payments,
              exp_delivery: orderData.exp_delivery ?? order.exp_delivery,
              remaining: orderData.remaining ?? order.remaining,
              round_off: orderData.round_off ?? order.round_off,
              note: orderData.note ?? order.note,
              user_id: orderData.user_id ?? order.user_id,
              by_user: orderData.by_user ?? order.by_user,
            })
            : order,
        ),
      );
    });

    // Listen for individual entry updates (fallback from assign karigar API)
    newSocket.on("custom_order_entry_updated", (data) => {
      // console.log("Socket received custom_order_entry_updated event:", data);

      if (!data?.custom_order_entry) return;

      const entryData = data.custom_order_entry;

      setOrders((prev) =>
        prev.map((order) => {
          // Find if this entry belongs to this order
          if (
            order.custom_order_entry &&
            Array.isArray(order.custom_order_entry)
          ) {
            const updatedEntries = order.custom_order_entry.map((entry) =>
              String(entry.id) === String(entryData.id)
                ? { ...entry, ...entryData }
                : entry,
            );

            // Check if any entry was actually updated
            const wasUpdated = updatedEntries.some(
              (entry, index) => entry !== order.custom_order_entry[index],
            );

            if (wasUpdated) {
              return { ...order, custom_order_entry: updatedEntries };
            }
          }
          return order;
        }),
      );
    });

    return () => {
      newSocket.off("custom_order_created");
      newSocket.off("custom_order_updated");
      newSocket.off("cancel_order_created");
      newSocket.off("custom_order_entry_updated");
      newSocket.disconnect();
    };
  }, []);

  // Handle item selection
  const handleItemSelect = (orderId, itemId, itemTagNo, isSelected) => {
    setSelectedItems((prev) => {
      const newSelection = { ...prev };
      if (!newSelection[orderId]) {
        newSelection[orderId] = {};
      }

      if (isSelected) {
        newSelection[orderId][itemId] = {
          tag_no: itemTagNo || "",
        };
      } else {
        delete newSelection[orderId][itemId];
        // Clean up empty order entries
        if (Object.keys(newSelection[orderId]).length === 0) {
          delete newSelection[orderId];
        }
      }

      return newSelection;
    });
  };

  // Check if an item is selected
  const isItemSelected = (orderId, itemId) => {
    return Boolean(selectedItems[orderId]?.[itemId]);
  };

  // Check if karigar is assigned (karigar_id not 0 means karigar assigned)
  const isKarigarAssigned = (item) => {
    const karigarId = item.karigar_id;
    if (karigarId === null || karigarId === undefined || karigarId === "") {
      return false;
    }
    return karigarId !== 0 && karigarId !== "0";
  };

  // Check if an item is already received (status = 2 means received)
  const isItemReceived = (item) => {
    const status = item?.status ?? item?.status_code ?? item?.legacy_status;
    return (
      status === 2 ||
      status === "2" ||
      parseInt(status, 10) === 2 ||
      Boolean(item?.received_date)
    );
  };

  const getReceivedItems = (order) =>
    Array.isArray(order?.custom_order_entry)
      ? order.custom_order_entry.filter(
        (item) => isItemReceived(item) && !isItemCancelled(item),
      )
      : [];

  const isItemCancelled = (item) => {
    return item.status === "3" || item.status === 3;
  };

  // Check if order is editable: not editable if ANY item has karigar assigned
  const isOrderEditable = (order) => {
    const items = order?.custom_order_entry || [];
    return !items.some((it) => isKarigarAssigned(it));
  };

  // Get selected items count for display
  const getSelectedItemsCount = () => {
    let count = 0;
    Object.values(selectedItems).forEach((orderItems) => {
      count += Object.keys(orderItems).length;
    });
    return count;
  };

  // Build a flat list of selected item objects from current orders
  const getSelectedItemsFlat = () => {
    const selected = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (!order || !Array.isArray(order.custom_order_entry)) return;
      const selectedItemIds = Object.keys(selectedItems[orderId] || {});
      order.custom_order_entry.forEach((item) => {
        if (selectedItemIds.includes(String(item.id))) {
          selected.push(item);
        }
      });
    });
    return selected;
  };

  // Determine whether all selected items are eligible for karigar assignment
  const canAssignSelectedItems = () => {
    const selected = getSelectedItemsFlat();
    if (selected.length === 0) return false;
    return selected.every(
      (item) =>
        !isKarigarAssigned(item) &&
        !isItemReceived(item) &&
        !isItemCancelled(item),
    );
  };

  // Sort orders by id (or created_at) descending for most recent first
  const sortedOrders = [...orders].sort((a, b) => {
    // Prefer created_at if available, else fallback to id
    if (a.created_at && b.created_at) {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return Number(b.id) - Number(a.id);
  });

  // Client-side status filter on the current server page
  const filteredOrders = sortedOrders.filter((order) => {
    const status = order.status || "";
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" && (status === "1" || status === 1)) ||
      (statusFilter === "Inactive" && (status === "0" || status === 0)) ||
      (statusFilter === "Cancelled" && (status === "3" || status === 3)) ||
      status === "";
    return matchesStatus;
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedOrders = filteredOrders;

  // View modal logic
  const handleViewClick = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  // Add stub for Edit button
  const handleEditClick = (order) => {
    // Navigate to edit page and pass only the custom order id
    navigate("/editcustomorderentry", {
      state: {
        customOrderId: order.id,
      },
    });
  };

  // Move received custom-order items to Sales
  const handleMoveToSales = async (order) => {
    if (!order?.id) {
      toast.error("Invalid custom order selected");
      return;
    }

    const selectedForOrder = Object.keys(selectedItems[order.id] || {});
    const listReceivedItems = getReceivedItems(order);

    if (listReceivedItems.length === 0 && selectedForOrder.length === 0) {
      toast.error("No received items available to move to Sales");
      return;
    }

    try {
      const response = await showCustomOrder({ id: order.id });
      const details = resolveCustomOrderShowPayload(response);
      const fullOrder =
        (isErpApiSuccess(response) || response?.message === "radha") && details
          ? details
          : order;

      const receivedItems = getReceivedItems(fullOrder);
      const itemsForSales =
        selectedForOrder.length > 0
          ? receivedItems.filter((item) =>
            selectedForOrder.includes(String(item.id)),
          )
          : receivedItems.length > 0
            ? receivedItems
            : listReceivedItems;

      if (itemsForSales.length === 0) {
        toast.error("No received items available to move to Sales");
        return;
      }

      const mappedItems = itemsForSales.map((item) => ({
        ...item,
        product_group:
          item.product_group ??
          item.product_group_id ??
          item.product_type ??
          "",
        product_group_name:
          item.product_group_name ||
          item.product_group ||
          item.product_type ||
          "",
      }));

      navigate("/sales", {
        state: {
          customOrderId: fullOrder.id || order.id,
          customOrderTable: mappedItems,
          customOrderData: fullOrder,
          customOrderPayments: fullOrder.payments || order.payments || [],
        },
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to load custom order for sales",
      );
    }
  };

  // Handle bulk assign karigar for selected items
  const handleBulkAssignKarigar = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("Please select at least one item");
      return;
    }

    // Prevent assigning when any selected item already has a karigar
    const selectedFlat = getSelectedItemsFlat();
    if (selectedFlat.some((item) => isKarigarAssigned(item))) {
      toast.error("Selected item(s) already have Karigar assigned");
      return;
    }

    // Get selected items with their order data
    const selectedItemsData = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && selectedItems[orderId]) {
        const selectedItemIds = Object.keys(selectedItems[orderId]);
        // Filter only selected items from the order
        const filteredItems =
          order.custom_order_entry?.filter((item) =>
            selectedItemIds.includes(String(item.id)),
          ) || [];

        if (filteredItems.length > 0) {
          selectedItemsData.push({
            ...order,
            custom_order_entry: filteredItems,
          });
        }
      }
    });

    // For the modal, we need to create a combined order data structure
    // If multiple orders are selected, we'll use the first order as base and combine items
    if (selectedItemsData.length > 0) {
      let combinedOrderData;

      if (selectedItemsData.length === 1) {
        // Single order, use as is
        combinedOrderData = selectedItemsData[0];
      } else {
        // Multiple orders, combine items
        combinedOrderData = {
          ...selectedItemsData[0],
          custom_order_entry: selectedItemsData.flatMap(
            (order) => order.custom_order_entry,
          ),
          id: "bulk_" + Date.now(), // Create a unique ID for bulk operation
          isBulkAssignment: true,
        };
      }

      setAssignKarigarOrderData(combinedOrderData);
      setShowAssignKarigarModal(true);
    }
  };

  // Handle assign karigar success
  const handleAssignKarigarSuccess = () => {
    // Clear selected items
    setSelectedItems({});

    // Refresh orders data to reflect any changes
    fetchOrders(currentPage, itemsPerPage, searchQuery);

    // Close modal
    setShowAssignKarigarModal(false);
    setAssignKarigarOrderData(null);
  };

  // Handle assign karigar modal close without success
  const handleAssignKarigarModalClose = () => {
    // Just close modal without clearing selected items
    setShowAssignKarigarModal(false);
    setAssignKarigarOrderData(null);
  };

  // Handle bulk receive & update for selected items
  const handleBulkReceiveUpdate = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("Please select at least one item");
      return;
    }

    // Get selected items with their order data
    const selectedItemsData = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && selectedItems[orderId]) {
        const selectedItemIds = Object.keys(selectedItems[orderId]);
        // Filter only selected items from the order
        const filteredItems =
          order.custom_order_entry?.filter((item) =>
            selectedItemIds.includes(String(item.id)),
          ) || [];

        if (filteredItems.length > 0) {
          selectedItemsData.push({
            ...order,
            custom_order_entry: filteredItems,
          });
        }
      }
    });

    // For the modal, we need to create a combined order data structure
    if (selectedItemsData.length > 0) {
      let combinedOrderData;

      if (selectedItemsData.length === 1) {
        // Single order, use as is
        combinedOrderData = selectedItemsData[0];
      } else {
        // Multiple orders, combine items
        combinedOrderData = {
          ...selectedItemsData[0],
          custom_order_entry: selectedItemsData.flatMap(
            (order) => order.custom_order_entry,
          ),
          id: "bulk_receive_" + Date.now(), // Create a unique ID for bulk operation
          isBulkReceive: true,
        };
      }

      setReceiveUpdateOrderData(combinedOrderData);
      setShowReceiveUpdateModal(true);
    }
  };

  // Handle receive & update success
  const handleReceiveUpdateSuccess = () => {
    // Clear selected items
    setSelectedItems({});

    // Refresh orders data to reflect any changes
    fetchOrders(currentPage, itemsPerPage, searchQuery);

    // Close modal
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  // Handle receive & update modal close without success
  const handleReceiveUpdateModalClose = () => {
    // Just close modal without clearing selected items
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  // Handle cancel order - check if items are selected
  const handleCancelOrder = () => {
    if (!canCancelOrder) {
      toast.error("You do not have permission to cancel orders.");
      return;
    }
    const selectedCount = getSelectedItemsCount();

    if (selectedCount > 0) {
      // Items are selected - cancel selected items
      const selectedItemsData = [];
      let firstOrder = null;

      Object.keys(selectedItems).forEach((orderId) => {
        const order = orders.find((o) => String(o.id) === String(orderId));
        if (order && selectedItems[orderId]) {
          if (!firstOrder) firstOrder = order;

          const selectedItemIds = Object.keys(selectedItems[orderId]);
          const filteredItems =
            order.custom_order_entry?.filter((item) =>
              selectedItemIds.includes(String(item.id)),
            ) || [];

          filteredItems.forEach((item) => {
            selectedItemsData.push({
              id: item.id,
              tag_no: item.tag_no || "",
              product_group_id: item.product_group,
              order_no: order.co_number || order.tag_no || order.id || "",
              product_group_name: item.product_group || "Product Group",
            });
          });
        }
      });

      if (selectedItemsData.length > 0 && firstOrder) {
        const trimmedOrder = {
          ...firstOrder,
          custom_order_entry: firstOrder.custom_order_entry?.filter((item) =>
            selectedItemsData.some(
              (selected) => String(selected.id) === String(item.id),
            ),
          ),
        };
        setCancelOrderData(trimmedOrder);
        setCancelSelectedItems(selectedItemsData);
        setIsWholeOrderCancel(false);
        setShowCancelOrderModal(true);
      }
    } else {
      // No items selected - show message
      toast.error(
        "Please select at least one item to cancel, or use the cancel button in the order row to cancel the whole order",
      );
    }
  };

  // Handle cancel whole order (when clicking on a specific order row)
  const handleCancelWholeOrder = (order) => {
    if (!canCancelOrder) {
      toast.error("You do not have permission to cancel orders.");
      return;
    }
    const orderSelection =
      selectedItems[String(order.id)] || selectedItems[order.id];

    if (orderSelection && Object.keys(orderSelection).length > 0) {
      // Use selected items for this order to perform item-level cancellation
      const selectedItemIds = Object.keys(orderSelection);
      const filteredItems =
        order.custom_order_entry?.filter((item) =>
          selectedItemIds.includes(String(item.id)),
        ) || [];

      const selectedItemsData = filteredItems.map((item) => ({
        id: item.id,
        tag_no: item.tag_no || "",
        order_no: order.co_number || order.tag_no || order.id || "",
        product_group_id: item.product_group,
        product_group_name: item.product_group || "Product Group",
      }));

      if (selectedItemsData.length === 0) {
        toast.error("Selected items not found for cancellation");
        return;
      }

      const trimmedOrder = {
        ...order,
        custom_order_entry: filteredItems,
      };

      // console.log("Cancel (Selected via Action) Order Data:", trimmedOrder);

      setCancelOrderData(trimmedOrder);
      setCancelSelectedItems(selectedItemsData);
      setIsWholeOrderCancel(false);
      setShowCancelOrderModal(true);
    } else {
      // No items selected; proceed with whole order cancellation
      setCancelOrderData(order);
      setCancelSelectedItems([]);
      setIsWholeOrderCancel(true);
      setShowCancelOrderModal(true);
    }
  };

  // Handle cancel order success
  const handleCancelOrderSuccess = () => {
    // Clear selected items
    setSelectedItems({});
    // Refresh orders
    fetchOrders(currentPage, itemsPerPage, searchQuery);
    // Close modal
    setShowCancelOrderModal(false);
    setCancelOrderData(null);
    setCancelSelectedItems([]);
  };

  const resetPaymentSettlementState = () => {
    setCustomSettlementRows(createDefaultSettlementRows());
    setExistingSettlementPayments([]);
    setSettlementOrderDetails(null);
    setSelectedOrderForSettlement(null);
    setIsSettlementLoading(false);
    setIsSettlementSaving(false);
    setIsPreviousPaymentsExpanded(false);
    settlementIdempotencyKeyRef.current = null;
  };

  const addCustomSettlementRow = () => {
    setCustomSettlementRows((prevRows) => [
      ...prevRows,
      {
        id: prevRows.length + 1,
        pay_mode: "",
        type: "",
        remarks: "",
        amount: "",
      },
    ]);
  };

  const handleCustomSettlementRowChange = (rowId, field, value) => {
    setCustomSettlementRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;
        if (field === "pay_mode" && value === "kasar") {
          return { ...row, pay_mode: value, type: "" };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  const handleCustomSettlementAmountFocus = (rowId) => {
    if (
      customSettlementRows.length > 0 &&
      rowId === customSettlementRows[customSettlementRows.length - 1].id
    ) {
      addCustomSettlementRow();
    }
  };

  const handleCustomSettlementRowRemove = (rowId) => {
    setCustomSettlementRows((prevRows) => {
      if (prevRows.length === 1) {
        return prevRows;
      }
      const filtered = prevRows
        .filter((row) => row.id !== rowId)
        .map((row, index) => ({ ...row, id: index + 1 }));
      return filtered.length > 0 ? filtered : createDefaultSettlementRows();
    });
  };

  const getBankNameById = (id) => {
    if (id === null || id === undefined || id === "" || id === "None") {
      return "-";
    }
    const bank = bankAccountsList.find(
      (bank) => String(bank.id) === String(id),
    );
    return bank
      ? bank.bank_name || bank.account_number || `Bank ${bank.id}`
      : "-";
  };

  const handleOpenPaymentSettlementModal = async (order) => {
    if (!order) return;

    setSelectedOrderForSettlement(order);
    setCustomSettlementRows(createDefaultSettlementRows());
    setExistingSettlementPayments([]);
    setSettlementOrderDetails(null);
    setIsPreviousPaymentsExpanded(false);
    setShowPaymentSettlementModal(true);
    setIsSettlementLoading(true);

    try {
      const response = await showCustomOrder({ id: order.id });

      const details = resolveCustomOrderShowPayload(response);

      if (
        (isErpApiSuccess(response) || response?.message === "radha") &&
        details
      ) {
        setSettlementOrderDetails(details);
        setExistingSettlementPayments(details.payments || []);
      } else {
        toast.error(response?.message || "Failed to load custom order details");
        setShowPaymentSettlementModal(false);
        resetPaymentSettlementState();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to load custom order details",
      );
      setShowPaymentSettlementModal(false);
      resetPaymentSettlementState();
    } finally {
      setIsSettlementLoading(false);
    }
  };

  const handleClosePaymentSettlementModal = () => {
    if (isSettlementSaving) {
      return;
    }
    setShowPaymentSettlementModal(false);
    resetPaymentSettlementState();
  };

  const bankOptions = useMemo(
    () =>
      bankAccountsList.map((bank) => ({
        value: String(bank.id),
        label: bank.bank_name || bank.account_number || `Bank ${bank.id}`,
      })),
    [bankAccountsList],
  );

  const settlementTotalAmount = useMemo(() => {
    if (settlementOrderDetails) {
      const totalValue =
        settlementOrderDetails.grand_total ??
        settlementOrderDetails.grandTotal ??
        settlementOrderDetails.total ??
        settlementOrderDetails.final_amount ??
        settlementOrderDetails.final_amt;
      if (totalValue !== undefined && totalValue !== null) {
        return parseNumericValue(totalValue);
      }
    }
    if (selectedOrderForSettlement) {
      return getCustomOrderGrandTotal(selectedOrderForSettlement);
    }
    return 0;
  }, [settlementOrderDetails, selectedOrderForSettlement]);

  const existingPaymentsTotal = useMemo(
    () =>
      existingSettlementPayments.reduce(
        (sum, payment) =>
          sum +
          parseNumericValue(
            payment.aavak_amt ??
            payment.total_amount ??
            payment.amt ??
            payment.amount ??
            0,
          ),
        0,
      ),
    [existingSettlementPayments],
  );

  const settlementRemainingAmount = useMemo(() => {
    if (
      settlementOrderDetails &&
      settlementOrderDetails.remaining !== undefined &&
      settlementOrderDetails.remaining !== null
    ) {
      return parseNumericValue(settlementOrderDetails.remaining);
    }
    if (
      settlementOrderDetails &&
      settlementOrderDetails.remaining_amount !== undefined &&
      settlementOrderDetails.remaining_amount !== null
    ) {
      return parseNumericValue(settlementOrderDetails.remaining_amount);
    }
    if (
      selectedOrderForSettlement &&
      selectedOrderForSettlement.remaining !== undefined &&
      selectedOrderForSettlement.remaining !== null
    ) {
      return parseNumericValue(selectedOrderForSettlement.remaining);
    }
    return Math.max(settlementTotalAmount - existingPaymentsTotal, 0);
  }, [
    settlementOrderDetails,
    selectedOrderForSettlement,
    settlementTotalAmount,
    existingPaymentsTotal,
  ]);

  const newSettlementAmountTotal = useMemo(
    () =>
      customSettlementRows.reduce(
        (sum, row) => sum + parseNumericValue(row.amount),
        0,
      ),
    [customSettlementRows],
  );

  const handleSavePaymentSettlement = async () => {
    if (!selectedOrderForSettlement) {
      toast.error("Unable to determine the selected order");
      return;
    }

    if (isSettlementLoading || isSettlementSaving) {
      return;
    }

    const newPayments = customSettlementRows
      .filter((row) => row.pay_mode && parseNumericValue(row.amount) > 0)
      .map((row) => ({
        pay_mode_key: row.pay_mode,
        pay_mode: SETTLEMENT_METHOD_LABELS[row.pay_mode] || row.pay_mode || "",
        bank_type: row.pay_mode === "kasar" ? "" : row.type || "",
        remarks: row.remarks || "",
        amt: parseNumericValue(row.amount),
      }));

    if (newPayments.length === 0) {
      toast.error("Enter at least one amount to settle");
      return;
    }

    const baseRemaining = settlementRemainingAmount;
    const additionalAmount = newSettlementAmountTotal;

    if (additionalAmount - baseRemaining > 0.05) {
      toast.error("Settlement amount exceeds remaining balance");
      return;
    }

    setIsSettlementSaving(true);

    try {
      if (!settlementIdempotencyKeyRef.current) {
        settlementIdempotencyKeyRef.current = createCustomOrderIdempotencyKey();
      }

      const erpPayments = newPayments
        .map((p) => {
          const remarks = p.remarks?.trim() ? p.remarks.trim() : null;
          if (p.pay_mode_key === "kasar") {
            return {
              payment_mode: "Kasar",
              amount: Number(p.amt) || 0,
              remarks,
            };
          }
          return {
            bank_account_id: Number(p.bank_type) || undefined,
            amount: Number(p.amt) || 0,
            payment_mode: p.pay_mode || null,
            remarks,
          };
        })
        .filter((p) => p.amount > 0);

      const data = await settlementCustomOrder({
        custom_order_id: selectedOrderForSettlement.id,
        idempotency_key: settlementIdempotencyKeyRef.current,
        payments: erpPayments,
      });

      if (isCustomOrdersApiSuccess(data) || isErpApiSuccess(data)) {
        settlementIdempotencyKeyRef.current = null;
        toast.success("Amount settled successfully");
        setShowPaymentSettlementModal(false);
        resetPaymentSettlementState();
        fetchOrders(currentPage, itemsPerPage, searchQuery);
      } else {
        toast.error(data?.message || "Failed to save settlement details");
      }
    } catch (error) {
      console.error("Error saving settlement:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to save settlement",
      );
    } finally {
      setIsSettlementSaving(false);
    }
  };

  const hasExistingSettlementPayments = existingSettlementPayments.length > 0;
  const disableSettlementSaveButton =
    isSettlementSaving || isSettlementLoading || newSettlementAmountTotal <= 0;

  return (
    <>
      <style>
        {`
          .item-checkbox {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          
          .item-column:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-row:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-checkbox:checked {
            opacity: 1 !important;
          }
          
          .custom-order-table-container {
            max-height: 60vh;
            overflow-y: auto;
          }

          .custom-order-table-container table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #fff;
            white-space: nowrap;
          }

          .item-scroll-container {
            padding: 4px;
          }
          
          .item-scroll-container::-webkit-scrollbar {
            width: 6px;
          }
          
          .item-scroll-container::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          
          .item-scroll-container::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          
          .item-scroll-container::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
          
          .item-scroll-container {
            scrollbar-width: thin;
            scrollbar-color: #c1c1c1 #f1f1f1;
          }

          .custom-order-active-row,
          .custom-order-active-row td {
            background-color: rgba(25, 135, 84, 0.03) !important;
          }

          .custom-order-inactive-row,
          .custom-order-inactive-row td {
            background-color: rgba(220, 53, 69, 0.03) !important;
          }

          .custom-order-cancelled-row,
          .custom-order-cancelled-row td {
            background-color: rgba(108, 117, 125, 0.05) !important;
          }

          .custom-order-default-row,
          .custom-order-default-row td {
            background-color: rgba(255, 193, 7, 0.03) !important;
          }
        `}
      </style>
      <div className={`body-wrapper ${cardClass}`}>
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Custom Order Table</h4>
              {getCurrentPinType() !== "quote" && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  onClick={() => navigate("/customorderentry")}
                >
                  Add Custom Order
                </button>
              )}
            </div>

            <div className="card-body p-4">
              {/* Search and Filter Section */}
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  style={{ maxWidth: "260px" }}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <div className="d-flex align-items-center gap-2">
                  {getSelectedItemsCount() > 0 && (
                    <>
                      <button
                        className={`btn btn-primary ${buttonClass}`}
                        onClick={handleBulkAssignKarigar}
                        title={
                          canAssignSelectedItems()
                            ? `Assign Karigar (${getSelectedItemsCount()})`
                            : "Selected item(s) already have Karigar assigned"
                        }
                        disabled={!canAssignSelectedItems()}
                      >
                        Assign Karigar ({getSelectedItemsCount()})
                      </button>
                      <button
                        className={`btn btn-primary ${buttonClass}`}
                        onClick={handleBulkReceiveUpdate}
                      >
                        Receive & Update ({getSelectedItemsCount()})
                      </button>
                    </>
                  )}
                  <select
                    className="form-select w-auto"
                    style={{ maxWidth: "200px" }}
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>{" "}
              {/* Table */}
              <div
                className={`table-responsive mb-4 border ${cardClass} custom-order-table-container`}
              >
                <table className={`table mb-0 align-middle ${tableClass}`}>
                  <thead className="text-dark fs-4">
                    <tr>
                      <th style={{ minWidth: "80px" }}>Order</th>
                      <th style={{ minWidth: "100px" }}>Created By</th>
                      <th style={{ minWidth: "120px" }}>Customer</th>
                      <th style={{ minWidth: "200px", maxWidth: "250px" }}>
                        Item
                      </th>
                      <th style={{ minWidth: "100px" }}>Delivery</th>
                      <th style={{ minWidth: "100px" }}>Total</th>
                      <th style={{ minWidth: "100px" }}>Adv. Paid</th>
                      <th style={{ minWidth: "80px" }}>Status</th>
                      <th style={{ minWidth: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="9" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : displayedOrders.length > 0 ? (
                      displayedOrders.map((order) => {
                        // Use customer fields from show_custom_order payload
                        const customerDisplay =
                          order.customer_name ||
                          order.name ||
                          order.user_name ||
                          "N/A";
                        const statusValue = String(order.status);
                        const rowClassName =
                          statusValue === "1"
                            ? "custom-order-active-row"
                            : statusValue === "0"
                              ? "custom-order-inactive-row"
                              : statusValue === "3"
                                ? "custom-order-cancelled-row"
                                : "custom-order-default-row";

                        const orderTotalAmount =
                          getCustomOrderGrandTotal(order);
                        const orderPaidAmount = getCustomOrderAdvPaid(order);
                        const orderRemainingAmount = Math.max(
                          orderTotalAmount - orderPaidAmount,
                          0,
                        );
                        const isOrderCancelled =
                          statusValue === "3" ||
                          (order.custom_order_entry?.length > 0 &&
                            order.custom_order_entry.every((item) =>
                              isItemCancelled(item),
                            ));

                        return (
                          <tr key={order.id} className={rowClassName}>
                            <td className="actions-cell">
                              {order.co_number ||
                                order.order_number ||
                                order.doc_number ||
                                order.tag_no ||
                                "N/A"}
                            </td>
                            <td>{order.employee_name || "N/A"}</td>
                            <td>{customerDisplay}</td>
                            <td
                              className="item-column"
                              style={{ maxWidth: "250px", minWidth: "200px" }}
                            >
                              {order.custom_order_entry &&
                                Array.isArray(order.custom_order_entry) &&
                                order.custom_order_entry.length > 0 ? (
                                <div
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "120px",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                  }}
                                  className="item-scroll-container"
                                >
                                  {order.custom_order_entry.map(
                                    (item, index) => (
                                      <div
                                        key={item.id || index}
                                        className="mb-1 d-flex align-items-start item-row"
                                        style={{ maxWidth: "100%" }}
                                      >
                                        {!isItemCancelled(item) &&
                                          !isItemReceived(item) && (
                                            <input
                                              type="checkbox"
                                              className="form-check-input me-2 item-checkbox"
                                              style={{
                                                flexShrink: 0,
                                                marginTop: "2px",
                                              }}
                                              checked={isItemSelected(
                                                order.id,
                                                item.id,
                                              )}
                                              onChange={(e) =>
                                                handleItemSelect(
                                                  order.id,
                                                  item.id,
                                                  item.tag_no,
                                                  e.target.checked,
                                                )
                                              }
                                            />
                                          )}
                                        <span
                                          style={{
                                            wordWrap: "break-word",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            lineHeight: "1.3",
                                            fontSize: "0.9em",
                                            flex: 1,
                                          }}
                                        >
                                          <div className="d-flex align-items-center flex-wrap gap-2">
                                            <strong
                                              style={{
                                                opacity: isItemCancelled(item)
                                                  ? 0.6
                                                  : 1,
                                              }}
                                            >
                                              {item.tag_no
                                                ? `${item.tag_no}`
                                                : "No Tag"}
                                            </strong>
                                            {isItemReceived(item) &&
                                              !isItemCancelled(item) && (
                                                <span
                                                  className="badge bg-success-subtle text-success"
                                                  style={{ fontSize: "0.7em" }}
                                                >
                                                  ✓ Received
                                                </span>
                                              )}
                                            {!isItemReceived(item) &&
                                              isKarigarAssigned(item) &&
                                              !isItemCancelled(item) && (
                                                <span
                                                  className="badge bg-info-subtle text-info"
                                                  style={{ fontSize: "0.7em" }}
                                                >
                                                  Karigar Assigned
                                                </span>
                                              )}
                                            {isItemCancelled(item) && (
                                              <span
                                                className="badge bg-danger-subtle text-danger"
                                                style={{ fontSize: "0.7em" }}
                                              >
                                                Cancelled
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              opacity: isItemCancelled(item)
                                                ? 0.6
                                                : 1,
                                            }}
                                          >
                                            {item.product_group_name ||
                                              "Product Group"}
                                          </div>
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              ) : (
                                "No items"
                              )}
                            </td>
                            <td>
                              {order.exp_delivery
                                ? formatDateToDDMMYYYY(order.exp_delivery)
                                : "N/A"}
                            </td>
                            <td>
                              {formatIndianRupee(
                                getCustomOrderGrandTotal(order),
                              )}
                            </td>
                            <td>
                              {orderPaidAmount > 0
                                ? formatIndianRupee(orderPaidAmount)
                                : "-"}
                            </td>
                            <td>
                              <span
                                className={`badge ${statusValue === "3"
                                  ? "bg-danger-subtle text-danger"
                                  : statusValue === "1"
                                    ? "bg-success-subtle text-success"
                                    : statusValue === "0"
                                      ? "bg-secondary-subtle text-secondary"
                                      : "bg-warning-subtle text-warning"
                                  }`}
                              >
                                {statusValue === "3"
                                  ? "Cancelled"
                                  : statusValue === "1"
                                    ? "Active"
                                    : statusValue === "0"
                                      ? "Inactive"
                                      : "Inactive"}
                              </span>
                            </td>
                            <td>
                              {/* <button
                                className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                                onClick={() => handleViewClick(order)}
                                title="View Slip"
                              >
                                <Icon
                                  icon="solar:eye-bold"
                                  width="16"
                                  height="16"
                                />
                              </button> */}
                              <div className="d-flex align-items-center gap-2 flex-nowrap">
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => {
                                    setSelectedOrderForPdf(order);
                                    setShowPdfModal(true);
                                  }}
                                  title="PDF Preview"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4.172 3.172C3 4.343 3 6.229 3 10v4c0 3.771 0 5.657 1.172 6.828S7.229 22 11 22h2c3.771 0 5.657 0 6.828-1.172S21 17.771 21 14v-4c0-3.771 0-5.657-1.172-6.828S16.771 2 13 2h-2C7.229 2 5.343 2 4.172 3.172M7.25 8A.75.75 0 0 1 8 7.25h8a.75.75 0 0 1 0 1.5H8A.75.75 0 0 1 7.25 8m0 4a.75.75 0 0 1 .75-.75h8a.75.75 0 0 1 0 1.5H8a.75.75 0 0 1-.75-.75M8 15.25a.75.75 0 0 0 0 1.5h5a.75.75 0 0 0 0-1.5z"
                                      clipRule="evenodd"
                                    ></path>
                                  </svg>
                                </button>
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() =>
                                    handleOpenPaymentSettlementModal(order)
                                  }
                                  title={
                                    isOrderCancelled
                                      ? "Payment not available for cancelled order"
                                      : orderRemainingAmount > 0
                                        ? "Payment Settlement"
                                        : "No remaining amount"
                                  }
                                  disabled={
                                    orderRemainingAmount <= 0 ||
                                    isOrderCancelled
                                  }
                                >
                                  <Icon
                                    icon="solar:wallet-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => handleEditClick(order)}
                                  title={
                                    isOrderEditable(order)
                                      ? "Edit"
                                      : "Edit disabled: Karigar assigned"
                                  }
                                  disabled={
                                    !isOrderEditable(order) ||
                                    order.custom_order_entry?.every((item) =>
                                      isItemCancelled(item),
                                    )
                                  }
                                >
                                  <Icon
                                    icon="solar:pen-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                                {(() => {
                                  const receivedItems = getReceivedItems(order);
                                  const selectedIds = Object.keys(
                                    selectedItems[order.id] || {},
                                  );
                                  const selectedReceivedItems =
                                    selectedIds.length > 0
                                      ? receivedItems.filter((item) =>
                                        selectedIds.includes(String(item.id)),
                                      )
                                      : [];
                                  const hasSaleableItems =
                                    selectedIds.length > 0
                                      ? selectedReceivedItems.length > 0 &&
                                      selectedReceivedItems.length ===
                                      selectedIds.length
                                      : receivedItems.length > 0;
                                  const btnTitle = hasSaleableItems
                                    ? "Move to Sales"
                                    : selectedIds.length > 0
                                      ? "Only received items can be moved to Sales"
                                      : "No received items available to move to Sales";
                                  return (
                                    <button
                                      className={`btn btn-primary btn-sm ${buttonClass}`}
                                      onClick={() => handleMoveToSales(order)}
                                      title={btnTitle}
                                      disabled={!hasSaleableItems}
                                    >
                                      <Icon
                                        icon="solar:cart-large-bold"
                                        width="16"
                                        height="16"
                                      />
                                    </button>
                                  );
                                })()}
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => handleCancelWholeOrder(order)}
                                  title={
                                    !canCancelOrder
                                      ? "No permission to cancel orders"
                                      : order.custom_order_entry?.every(
                                        (item) => isItemCancelled(item),
                                      )
                                        ? "Order already cancelled"
                                        : "Cancel Order"
                                  }
                                  disabled={
                                    !canCancelOrder ||
                                    order.custom_order_entry?.every((item) =>
                                      isItemCancelled(item),
                                    )
                                  }
                                >
                                  <Icon
                                    icon="solar:close-circle-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No custom orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <Pagination
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </div>
        </div>

        <Modal
          show={showPaymentSettlementModal}
          onHide={handleClosePaymentSettlementModal}
          centered
          size="xl"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Payment Settlement</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-3 pb-1 px-4">
            {isSettlementLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        Total Amount
                      </div>
                      <div className="fw-bold fs-5 mt-1 ">
                        {formatIndianRupee(settlementTotalAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        Remaining Amount
                      </div>
                      <div
                        className="fw-bold fs-5 mt-1"
                        style={{ color: "var(--theme-primary)" }}
                      >
                        {formatIndianRupee(settlementRemainingAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        New Payment Total
                      </div>
                      <div className="fw-bold fs-5 mt-1">
                        {formatIndianRupee(newSettlementAmountTotal)}
                      </div>
                    </div>
                  </div>
                </div>

                {hasExistingSettlementPayments ? (
                  <div className="mb-3">
                    <div
                      className={`d-flex justify-content-between align-items-center border ${buttonClass} px-3 py-2 mb-2 bg-white`}
                    >
                      <h6 className="fw-semibold mb-0 text-dark text-uppercase small">
                        Previous Payments
                      </h6>
                      <button
                        type="button"
                        className={`btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 ${buttonClass}`}
                        onClick={() =>
                          setIsPreviousPaymentsExpanded((prev) => !prev)
                        }
                      >
                        <Icon
                          icon={
                            isPreviousPaymentsExpanded
                              ? "solar:eye-closed-bold"
                              : "solar:eye-bold"
                          }
                          width={18}
                          height={18}
                        />
                        {isPreviousPaymentsExpanded ? "Hide" : "View"}
                      </button>
                    </div>
                    {isPreviousPaymentsExpanded && (
                      <div
                        className={`table-responsive rounded-0 border ${buttonClass} overflow-hidden`}
                      >
                        <table className="table table-sm mb-0 align-middle">
                          <thead className="bg-body-tertiary text-secondary small text-uppercase">
                            <tr>
                              <th
                                className="text-center"
                                style={{ width: "60px" }}
                              >
                                #
                              </th>
                              <th style={{ width: "140px" }}>Date</th>
                              <th>Mode</th>
                              <th>Type</th>
                              <th>Remarks</th>
                              <th
                                className="text-end"
                                style={{ width: "140px" }}
                              >
                                Amt
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingSettlementPayments.map(
                              (payment, index) => {
                                const paymentDate =
                                  payment.transaction_date ||
                                  payment.payment_date ||
                                  payment.created_at ||
                                  payment.date ||
                                  "";
                                const formattedDate = paymentDate
                                  ? formatDateToDDMMYYYY(
                                    String(paymentDate).split(/[ T]/)[0],
                                  )
                                  : "-";
                                return (
                                  <tr key={payment.id || index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{formattedDate}</td>
                                    <td>
                                      {payment.payment_mode ||
                                        payment.pay_mode ||
                                        payment.method ||
                                        "-"}
                                    </td>
                                    <td>
                                      {payment.bank_name ||
                                        getBankNameById(
                                          payment.bank_account_id ??
                                          payment.bank_type,
                                        )}
                                    </td>
                                    <td>{payment.remarks || "-"}</td>
                                    <td className="text-end">
                                      {formatIndianRupee(
                                        parseNumericValue(
                                          payment.amount ??
                                          payment.aavak_amt ??
                                          payment.total_amount ??
                                          payment.amt ??
                                          0,
                                        ),
                                      )}
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3 small text-muted">
                    No previous settlement payments recorded for this order.
                  </div>
                )}

                <h6 className="fw-semibold mb-2 text-dark text-uppercase small">
                  Add New Payments
                </h6>
                <div
                  className={`table-responsive rounded-0 border ${buttonClass} overflow-hidden`}
                >
                  <table className="table table-sm align-middle mb-0">
                    <thead className="bg-primary-subtle text-secondary small text-uppercase">
                      <tr>
                        <th className="text-center" style={{ width: "60px" }}>
                          #
                        </th>
                        <th>Mode <span className="text-danger">*</span></th>
                        <th style={{ width: "220px" }}>Type <span className="text-danger">*</span></th>
                        <th style={{ minWidth: "160px" }}>Remarks</th>
                        <th className="text-end" style={{ width: "150px" }}>
                          Amt <span className="text-danger">*</span>
                        </th>
                        <th className="text-center" style={{ width: "80px" }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {customSettlementRows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="text-center">{index + 1}</td>
                          <td>
                            <Select
                              options={SETTLEMENT_METHOD_OPTIONS}
                              value={
                                SETTLEMENT_METHOD_OPTIONS.find(
                                  (opt) => opt.value === row.pay_mode,
                                ) || null
                              }
                              onChange={(option) =>
                                handleCustomSettlementRowChange(
                                  row.id,
                                  "pay_mode",
                                  option ? option.value : "",
                                )
                              }
                              placeholder="Select Method"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base) => ({
                                  ...base,
                                  minHeight: "31px",
                                  height: "31px",
                                }),
                              }}
                              classNamePrefix="react-select"
                            />
                          </td>
                          <td>
                            {row.pay_mode === "kasar" ? (
                              <span className="text-muted small px-1">—</span>
                            ) : (
                              <Select
                                options={bankOptions}
                                value={
                                  bankOptions.find(
                                    (opt) => opt.value === row.type,
                                  ) || null
                                }
                                onChange={(option) =>
                                  handleCustomSettlementRowChange(
                                    row.id,
                                    "type",
                                    option ? option.value : "",
                                  )
                                }
                                placeholder="Select Type"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "31px",
                                    height: "31px",
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className={`shadow-none ${buttonClass}`}
                              type="text"
                              placeholder="Remarks"
                              value={row.remarks}
                              onChange={(e) =>
                                handleCustomSettlementRowChange(
                                  row.id,
                                  "remarks",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className={`shadow-none text-end ${buttonClass}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.amount}
                              onChange={(e) =>
                                handleCustomSettlementRowChange(
                                  row.id,
                                  "amount",
                                  e.target.value,
                                )
                              }
                              onFocus={() =>
                                handleCustomSettlementAmountFocus(row.id)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-secondary"
                              style={{
                                borderColor: "var(--theme-primary)",
                                color: "var(--theme-primary)",
                              }}
                              size="sm"
                              disabled={customSettlementRows.length === 1}
                              onClick={() =>
                                handleCustomSettlementRowRemove(row.id)
                              }
                            >
                              <Icon
                                icon="solar:trash-bin-minimalistic-bold"
                                width={16}
                                height={16}
                                style={{ color: "var(--theme-primary)" }}
                              />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-end">
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleSavePaymentSettlement}
              disabled={disableSettlementSaveButton}
            >
              {isSettlementSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Assign Karigar Modal */}
        <AssignKarigarModal
          show={showAssignKarigarModal}
          onHide={handleAssignKarigarModalClose}
          orderData={assignKarigarOrderData}
          onAssignSuccess={handleAssignKarigarSuccess}
        />

        {/* Receive & Update Modal */}
        <ReceiveUpdateModal
          show={showReceiveUpdateModal}
          onHide={handleReceiveUpdateModalClose}
          orderData={receiveUpdateOrderData}
          onReceiveSuccess={handleReceiveUpdateSuccess}
          masterGoldRate={masterGoldRate}
          masterSilverRate={masterSilverRate}
        />

        {/* Cancel Order Modal */}
        <CancelOrderModal
          show={showCancelOrderModal}
          onHide={() => {
            setShowCancelOrderModal(false);
            setCancelOrderData(null);
            setCancelSelectedItems([]);
          }}
          orderData={cancelOrderData}
          selectedItems={cancelSelectedItems}
          isWholeOrder={isWholeOrderCancel}
          orderType="custom"
          onSuccess={handleCancelOrderSuccess}
          bankAccountsList={bankAccountsList}
        />

        {/* Custom Order PDF Modal */}
        <CustomOrderPdfModal
          show={showPdfModal}
          onHide={() => {
            setShowPdfModal(false);
            setSelectedOrderForPdf(null);
          }}
          orderId={selectedOrderForPdf?.id}
        />
      </div>
    </>
  );
}

export default CustomOrderEntryTable;
